// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import type {VNode} from 'snabbdom';
import isFunction from 'lodash/isFunction';

import {SvgIconEl} from 'common/components/svg-icon';
import {SpinnerEl} from 'common/components/spinner';
import {ExperimentalTooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {unwrapSection} from 'common/components/items-menu/utils';
import {LocalizationStore} from 'common/i18n/store';

import {
  ItemAndEventHandler,
  ItemHandler,
  SectionItemContextPanelTransform,
  SelectedTheme,
  WrappedSection,
  WrappedSectionItem,
} from '../types';

const TooltipWrapper = ({item}: {item: WrappedSectionItem}, sectionItemElChild) => {
  const sectionItem = item.wrapped;
  // for items with nested sections, avoid displaying the tooltip on hover
  if (!sectionItem.tooltip?.text || (sectionItem.subSections && !sectionItem.isDisabled)) {
    return sectionItemElChild;
  }

  return (
    <>
      <ExperimentalTooltipEl
        attrs={{
          placement: Placement.Right,
          role: `tooltip`,
          ...(sectionItem.tooltip ?? {}),
        }}
        params={{text: sectionItem.tooltip.text}}
      />
      <section sel='.list-option-wrapper'>{sectionItemElChild}</section>
    </>
  );
};

export const SectionRowEl = (
  {
    allowPointerEventsAlways,
    item,
    isFocused,
    selectedTheme,
    contextPanelTransform,
    onClick,
    onMouseEnter,
    i18n,
  }: {
    allowPointerEventsAlways: boolean;
    item: WrappedSectionItem;
    isFocused: boolean;
    selectedTheme: SelectedTheme;
    contextPanelTransform: SectionItemContextPanelTransform;
    onClick: ItemAndEventHandler;
    onMouseEnter: ItemHandler;
    i18n: LocalizationStore;
  },
  sectionItemElChild?: VNode,
) => {
  const {print} = i18n;
  const sectionItem = item.wrapped;
  const hasSubSections = Boolean(sectionItem.subSections && sectionItem.subSections.length);
  const isClickEnabled = !(sectionItem.isDisabled || sectionItem.isReadOnly);
  return (
    <div class={{highlight: sectionItem.highlightItem}} attrs={{elref: `itemsMenuSectionRow`}}>
      <li
        sel='.list-option'
        class={{
          ...(!sectionItem.expansionToggle && {[`for-${selectedTheme}-selected-theme`]: true}),
          disabled: sectionItem.isDisabled,
          'special-action': sectionItem.isSpecialAction,
          expander: sectionItem.isExpander,
          focused: isFocused,
          'read-only': sectionItem.isReadOnly,
          selectable: !(sectionItem.isDisabled || sectionItem.isReadOnly),
          selected: sectionItem.isSelected && !sectionItem.suppressSelectedHighlight,
          'has-tooltip': Boolean(sectionItem.tooltip && sectionItem.tooltip.text),
          'allow-pointer-events': Boolean(allowPointerEventsAlways || contextPanelTransform),
          'has-sub-sections': hasSubSections,
          [`label-theme-${sectionItem.labelTheme}`]: Boolean(sectionItem.labelTheme),
        }}
        attrs={{
          elref: sectionItem.elref,
          role: `listitem`,
          'aria-label': print(sectionItem.label),
        }}
        on={{
          click: isClickEnabled && ((ev) => onClick(item, ev)),
          mouseenter: () => onMouseEnter(item),
        }}
      >
        <TooltipWrapper item={item}>{sectionItemElChild}</TooltipWrapper>
      </li>
    </div>
  );
};

export const SectionEl = (
  {
    section,
    sectionIdx,
    hideSectionTitle = false,
    areSectionItemsHidden = false,
    numOriginalItems,
    i18n,
    sectionLeftIcon = ``,
  }: {
    section: WrappedSection;
    sectionIdx: number;
    hideSectionTitle?: boolean;
    areSectionItemsHidden?: boolean;
    numOriginalItems: number;
    i18n: LocalizationStore;
    sectionLeftIcon: string;
  },
  sectionItemElChildren?: VNode[],
) => {
  const {sectionLabelIconRenderer, label, tooltip, icon, isLoading} = section;
  const {print} = i18n;
  const sectionHasHeader = Boolean(!hideSectionTitle && label);
  const sectionHasLeftIcon = Boolean(sectionLeftIcon);
  return (
    <div
      sel='.section-container'
      class={{
        'has-expansion-toggle': Boolean(section.expansionToggle),
        'has-header': sectionHasHeader,
        sticky: Boolean(section.isSticky),
      }}
    >
      {sectionIdx > 0 && (!section.hideTopDivider || section.expansionToggle) && (
        <div sel='.section-divider' class={{'has-divider-line': !section.hideTopDivider}} />
      )}
      {sectionHasHeader && (
        <div sel='.section-header' class={{'has-left-icon': sectionHasLeftIcon}}>
          {sectionHasLeftIcon && (
            <div sel='.icon-wrapper'>
              <SvgIconEl attrs={{icon: sectionLeftIcon, height: `18px`}} />
            </div>
          )}
          <span sel='.label'>{print(label)}</span>
          {isFunction(sectionLabelIconRenderer) && sectionLabelIconRenderer(unwrapSection(section))}
          {icon ? (
            <div sel='.section-icon.item-icon' attrs={{icon}}>
              {tooltip && (
                <ExperimentalTooltipEl
                  attrs={{
                    placement: Placement.RightCenter,
                    ...(tooltip ?? {}),
                  }}
                  params={{text: tooltip.text}}
                />
              )}
              <SvgIconEl attrs={{icon, empty: !icon}} />
            </div>
          ) : null}
        </div>
      )}
      {isLoading ? (
        <div sel='.loading-items'>
          <SpinnerEl
            attrs={{
              elref: `itemsMenuLoadingSpinner`,
            }}
          />
        </div>
      ) : (
        <ul sel='.screen-options-list' attrs={{role: `list`}}>
          {sectionItemElChildren}
        </ul>
      )}
      {!areSectionItemsHidden && !numOriginalItems && section.emptySectionRenderer && (
        <div sel='.empty-section-renderer'>{section.emptySectionRenderer()}</div>
      )}
    </div>
  );
};
